import type { StateCreator } from 'zustand';
import { contextForPerspective, historyText, inclusiveEnd, paintServerTurn, withoutReadingCard } from './landingAgentSlice';
import { withoutStreaming } from './answerStream';
import { withoutLiveProgress } from './progress';
import { needsRender, storedTurns } from './storedTurn';
import { previewOf } from './writerPersisted';
import type { ConversationThread } from '../../types';
import type { VoyagerState } from '../useVoyagerStore';
import {
  closeThread as apiCloseThread,
  deleteThread as apiDeleteThread,
  getThread,
  listThreads,
  patchThread,
  reopenThread as apiReopenThread,
  threadCursor,
  threadRowToConversation as rowToConversation,
  touchThread,
  upsertThread as apiUpsertThread, type ThreadContext } from '../../apis';
import type { ThreadRow } from '../../apis';
import { generateId } from '../../utils';
import { clearDrafts } from '../../components/LandingAgent/composerDrafts';
import { redactClaimWords } from '../../components/Agents/redact';
import { confirmParkedAgentChatTurns, getPersistenceNamespace, loadPins, loadParkedAgentChats, parkAgentChatSnapshot, unparkAgentChatSnapshot } from '../helpers/persistenceHelpers';
import { loadThreads as loadLegacyThreads, persistThreads as persistLegacyThreadsRaw } from '../helpers/persistenceHelpers';

// F4 (#1858 §1a, §4b; round 1 C-B3, A-B1): the client's two persistence sinks — the server's snapshot and the
// localStorage buffer — store every turn in its stored form (`storedTurn`): no catalog label, no undecided row or SQL
// — and (F1, #1806) no Writer words: a Writer answer as the server stores it, and a Writer turn's preview its question
const upsertThread = (id: string, body: Parameters<typeof apiUpsertThread>[1]) => apiUpsertThread(id, body?.snapshot
  && Array.isArray((body.snapshot as any).turns) ? { ...body, snapshot: { ...body.snapshot, turns: storedTurns((body.snapshot as any).turns) },
    ...('preview' in body ? { preview: previewOf((body.snapshot as any).turns, (body as any).preview) as any } : {}) } : body);
const persistLegacyThreads = (threads: ConversationThread[]) => persistLegacyThreadsRaw(threads.map((t) => (
  Array.isArray(t.landingAgentTurns) ? { ...t, landingAgentTurns: storedTurns(t.landingAgentTurns),
    preview: previewOf(t.landingAgentTurns, t.preview) as any } : t)));

/** A server row as a thread: a turn the answer gate held is painted from the frames the server rebuilt (#1174). */
const threadRowToConversation = (row: ThreadRow): ConversationThread => {
  const thread = rowToConversation(row);
  return { ...thread, landingAgentTurns: (thread.landingAgentTurns || []).map(paintServerTurn) };
};

/**
 * A thread's turns rebuilt from its own read (#1270). A conversation's turns are append-only on the server (every
 * write unions by turn id, `threads.store.merge_snapshot`; no route removes or reorders a turn — only a whole thread
 * is deleted), so the read holds every turn the server has, and the rebuild NEVER drops a turn: every fetched turn,
 * in the server's order, with its answer — then every local turn whose (unique, non-empty) id the read lacks, in
 * local order, exactly as it is here (answered, or in flight). A listed turn still pending is the server's own and
 * so is in the read. Null — keep the local turns as they are — when a local turn of this tab's own cannot be told
 * apart from the read's: one with no id, or an id it shares with another local turn or with two of the read's.
 *
 * Monotonic: a fetched turn replaces only a local `answerPending` placeholder (the list's question). A local turn
 * that has its own answer, a terminal status or is in flight here is kept as it is, at the server's position — a
 * read that is older than this tab's own answer never takes it back. The result is checked before it is returned:
 * every local turn id and every local turn that is not a placeholder must still be in it, or nothing is rebuilt.
 */
/** A local turn the server's read takes the place of: the list's question (`answerPending`), or an ended answer whose
 *  F4 frames are only references (a copy from the client's stored form, #1858 round 2: rendered by the read). */
const placeholder = (t: any) => !!t?.answerPending || needsRender(t);

export function rebuildTurns(local: any[], fetched: any[]): any[] | null {
  const unique = (turns: any[]) => {
    const counts = new Map<unknown, number>();
    for (const t of turns) counts.set(t?.id, (counts.get(t?.id) || 0) + 1);
    return (t: any) => typeof t?.id === 'string' && t.id !== '' && counts.get(t.id) === 1;
  };
  const localKey = unique(local);
  const fetchedKey = unique(fetched);
  const fetchedIds = new Set(fetched.map((t) => t?.id));
  const mine = new Map<string, any>();
  const kept: any[] = [];
  for (const t of local) {
    if (localKey(t)) {
      if (!fetchedIds.has(t.id)) kept.push(t);
      else if (!placeholder(t)) {
        if (!fetched.some((f) => f?.id === t.id && fetchedKey(f))) return null;   // the read holds it twice
        mine.set(t.id, t);
      }
    } else if (!placeholder(t)) {
      return null;
    }
  }
  const rebuilt = [...fetched.map((f) => (fetchedKey(f) && mine.has(f.id) ? mine.get(f.id) : f)), ...kept];
  // the invariant: nothing local went backwards — every local id, and every local turn that is not a placeholder
  const ids = new Set(rebuilt.map((t) => t?.id));
  const objects = new Set(rebuilt);
  if (local.some((t) => (localKey(t) && !ids.has(t.id)) || (!placeholder(t) && !objects.has(t)))) return null;
  return rebuilt;
}


/** The thread kinds whose transcript is a client-owned chat snapshot: an agent chat (#202) or a group chat (#307). */
export type ChatKind = 'agent_chat' | 'group';
export const isChatKind = (kind: unknown): kind is ChatKind => kind === 'agent_chat' || kind === 'group';

const THREADS_PAGE = 200;
/** The server keeps at most this many characters of a title (`TITLE_MAX`). */
const TITLE_MAX = 200;

/**
 * Durable threads (#181).
 *
 * A conversation is a server-side object from its first answer: it can be
 * named, starred, closed, reopened and resumed mid-analysis, and it survives a
 * reload. `savedThreads` holds every thread the server knows for this account
 * and workspace — open and closed — and the views derive what they show
 * (`isOpenThread`). Every mutation is optimistic: the list updates first and
 * the API call follows, so the UI never waits on a round trip to reflect
 * something the person just did.
 *
 * Names are kept from the localStorage era (`persistCurrentThread`,
 * `loadThread`, `renameThread`, `deleteThread`) so the callers in the landing
 * agent slice, the sidebar and History did not have to change.
 */
export interface ThreadsSlice {
  savedThreads: ConversationThread[];
  currentThreadId: string | null;
  threadsLoaded: boolean;
  /** Set when the last fetch failed; cleared when one succeeds. */
  threadsError: string | null;
  /**
   * A thread whose listed answers its own read is filling (#1270): `loading` holds the composer's send — a question
   * never goes out without the earlier answers' context — and `failed` lets it go with a notice that they are missing.
   */
  threadAnswers: Record<string, 'loading' | 'failed'>;
  /**
   * Bumped on every identity/workspace switch. Every async result checks it
   * before touching the store, so a fetch or persist that started under the
   * previous account or org can never land its transcript in this one.
   */
  threadsGeneration: number;

  fetchThreads: () => Promise<void>;
  /** The workspace changed: forget every thread AND the conversation on screen. */
  resetThreadsWorkspace: () => void;
  /** Persist the conversation on screen — or, given an id, a parked one whose answer just landed. */
  persistCurrentThread: (threadId?: string) => void;
  clearLandingChat: () => void;

  loadThread: (threadId: string) => void;
  /** Resolves true once the server has the name, false when the rename was rolled back — a start that must carry it waits for it. */
  renameThread: (threadId: string, title: string) => Promise<boolean>;
  starThread: (threadId: string, starred: boolean) => void;
  /** Resolves true once the server has closed it, false when the close was rolled back (#765: the sidebar's Undo waits for this). */
  closeThread: (threadId: string) => Promise<boolean>;
  /** Resolves true once the server has reopened it, false when the reopen was rolled back. */
  reopenThread: (threadId: string) => Promise<boolean>;
  deleteThread: (threadId: string) => void;
  addThreadToSpace: (threadId: string, spaceId: string | null) => void;
  /** A `?thread=<id>` deep link (#189): open it once the list has it. */
  pendingThreadOpen: string | null;
  openThreadWhenLoaded: (threadId: string) => void;
  /**
   * Create an agent chat's thread (#202) on the server, on the thread's own
   * write queue and behind the workspace guard: every later write for it
   * waits its turn, and an org switch mid-flight cannot create it in the
   * wrong workspace. Resolves false when the server never took it — the
   * tab is gone by then.
   */
  createAgentChatThread: (thread: ConversationThread) => Promise<boolean>;
  /**
   * Store an agent chat's transcript (#202) — the thread's `snapshot.turns`,
   * client-owned. The latest snapshot per thread waits in an outbox: a
   * write that fails is retried with backoff (2s, 5s, 15s) and again on
   * the next turn, and the thread is marked unsaved until one lands.
   */
  persistAgentChat: (threadId: string, turns: any[]) => void;
  /** Agent chats whose latest snapshot the server has not taken yet. */
  agentChatUnsaved: Record<string, true>;
  /** The objective a NEW conversation is asked inside (#766), until its thread is stored with it. */
  pendingThreadObjective: string | null;
  /** Opening an objective's space presets the composer's pick to the objective's perspective (#766, #749). */
  presetObjectivePick: (perspectiveId: string | null | undefined) => void;
  /** Ask a question inside an objective (#766): a new thread scoped to it. Returns whether the question started. */
  askInObjective: (objectiveId: string, text: string) => boolean;
  /** Tie a stored thread to an objective (#766: Track this as an objective, once the objective is committed). */
  tieThreadToObjective: (threadId: string, objectiveId: string) => Promise<boolean>;
}

function restoredHistoryOf(turns: any[]) {
  const restoredHistory: Array<{ role: 'user' | 'assistant'; content: string; turnId?: string }> = [];
  for (const t of turns) {
    if (t?.userText) restoredHistory.push({ role: 'user', content: t.userText, turnId: t.id });
    const res = t?.result || {};
    // the transcript is rebuilt from the retained turns by reference (#296): a claim's words never ride it
    const assistantContent = historyText((res.synthesis?.headline || res.formatterTitle || res.formatterSummary || '').toString().slice(0, 400), res);
    if (assistantContent) restoredHistory.push({ role: 'assistant', content: assistantContent, turnId: t.id });
  }
  return restoredHistory;
}

/** The last answered turn's scope, as the server wrote it: the ladder's context, else a walk's target. */
export function restoredContextOf(turns: any[], perspective: string | null): ThreadContext | null {
  for (let i = turns.length - 1; i >= 0; i--) {
    const res = turns[i]?.result || {};
    // a scope written under another perspective than the thread's now keeps only its window
    if (res.ladder?.thread_context) return contextForPerspective(res.ladder.thread_context as ThreadContext, perspective);
    const t = res.diagnose?.target;
    if (t?.metric) {
      return contextForPerspective({ perspective: turns[i]?.perspectiveId ?? perspective, metrics: [t.metric], primary_metric: t.metric, dimensions: [],
        filters: Array.isArray(t.filters) ? t.filters.filter((f: any) => f && typeof f === 'object') : [],
        window: t.window_start && t.window_end ? { start: String(t.window_start).slice(0, 10), end: inclusiveEnd(String(t.window_end)) } : null,
        definitions: [], session_id: turns[i]?.sessionId ?? null }, perspective);
    }
  }
  return null;
}

export function isOpenThread(t: ConversationThread): boolean {
  return !t.closedAt;
}

/** Open threads, starred first, most recently changed first within each. */
export function openThreadsInOrder(threads: ConversationThread[]): ConversationThread[] {
  return threads
    .filter(isOpenThread)
    .sort((a, b) => {
      if (Boolean(a.starred) !== Boolean(b.starred)) return a.starred ? -1 : 1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
}

function warn(action: string, err: unknown) {
  console.warn(`[threads] ${action} failed`, (err as any)?.message || err);
}

/**
 * Every write for one thread runs after the previous one. Two persists fire
 * close together on every answer (`mission_complete`, then the `ladder`
 * event); if they raced, the older request could commit last and the server
 * would keep the transcript without the ladder until the next reload. A
 * rename, star, close or delete goes on the same chain, so it cannot reach
 * the server before the row it targets exists — and a delete cannot be
 * undone by a creation that was still in flight.
 */
/** Thrown (and swallowed) when a queued write outlived its workspace. */
export class StaleWorkspaceError extends Error {
  constructor() { super('workspace changed before the write ran'); this.name = 'StaleWorkspaceError'; }
}

export const createThreadsSlice: StateCreator<
  VoyagerState, [], [], ThreadsSlice
> = (set, get) => {
  const chains = new Map<string, Promise<unknown>>();
  /** The latest agent-chat snapshot per thread that the server has not confirmed (#202). */
  const chatOutbox = new Map<string, {
    turns: any[]; attempt: number; timer: ReturnType<typeof setTimeout> | null;
    /** Captured when the snapshot was queued: a park after a workspace switch goes to the namespace it belongs to. */
    ns: string | null; title: string; source: { kind: string; id: string | null };
      kind?: ChatKind;
  }>();
  const CHAT_RETRY_MS = [2000, 5000, 15000];
  let retryDelay = 0;
  let fetchAttempt = 0;
  let migrateRetry = 0;

  /**
   * Threads with a change the server has not answered yet. This — not a
   * timestamp — decides who wins when a fetch and a local change overlap:
   * a client clock that runs ahead must not make settled local state beat
   * newer server rows, or keep a thread another tab deleted.
   */
  const inFlight = new Map<string, number>();
  const begin = (id: string) => inFlight.set(id, (inFlight.get(id) || 0) + 1);
  const settle = (id: string) => {
    const n = (inFlight.get(id) || 1) - 1;
    if (n <= 0) inFlight.delete(id); else inFlight.set(id, n);
  };
  const isInFlight = (id: string) => (inFlight.get(id) || 0) > 0;
  const applyServerStamp = (id: string, row: unknown) => {
    const stamp = (row as ThreadRow | undefined)?.updated_at;
    if (!stamp) return;
    const savedThreads = get().savedThreads.map((t) => (t.id === id ? { ...t, updatedAt: stamp } : t));
    set({ savedThreads } as Partial<VoyagerState>);
  };

  /**
   * A write that waits its turn behind every earlier write for the same
   * thread AND checks, when its turn comes, that the workspace it was made
   * for is still the active one. A request issued after a switch would carry
   * the new identity's token and write the previous workspace's data into
   * this one — so a stale write never starts.
   */
  const queued = <T,>(threadId: string, work: () => Promise<T>): Promise<T> => {
    const gen = get().threadsGeneration;
    const run = () => {
      if (get().threadsGeneration !== gen) return Promise.reject(new StaleWorkspaceError());
      return work();
    };
    const prev = chains.get(threadId) || Promise.resolve();
    const next = prev.then(run, run);
    chains.set(threadId, next.catch(() => undefined));
    return next;
  };

  /**
   * End the conversation on screen. Goes through the landing agent's own
   * reset, which invalidates the current attempt and marks the socket
   * abandoned before closing it — closing the socket directly would leave
   * the attempt live, and its `onclose` would reconnect the old question
   * under whatever identity is active by then.
   */
  const endConversation = (settle = true) => {
    // A run in flight on screen is stopped and stored as cancelled before
    // the reset abandons its socket — its snapshot was stored as running
    // when it was asked. Not on a workspace switch: nothing from the old
    // workspace may be written under the new one.
    const st = get() as any;
    const turns = st.landingAgentTurns || [];
    // Waiting on a clarification or a definition is in flight too: the
    // socket is open for an answer that will never come once this closes.
    const last = turns.length ? turns[turns.length - 1].status : null;
    if (settle && !turns[turns.length - 1]?.analysisOperationId && !turns[turns.length - 1]?.diagnosisOperationId && (last === 'running' || last === 'clarifying' || last === 'defining')) {
      get().cancelLandingAgent();
    }
    get().resetLandingAgent();
    set({ currentThreadId: null } as Partial<VoyagerState>);
  };

  /**
   * Leave the conversation on screen without ending it (#182): a run in
   * flight keeps going in its tab, its events land on the parked copy, and
   * the tab shows a dot when it finishes. For switching tabs and going Home;
   * close, delete and a workspace switch still END the conversation.
   */
  const parkScreen = () => {
    if (get().currentThreadId) get().persistCurrentThread();
    get().parkConversation();
    set({ currentThreadId: null } as Partial<VoyagerState>);
  };

  /**
   * Leaving the conversation on screen parks it — the tab strip shows the
   * run carrying on; only close/delete end a run.
   */
  const leaveScreen = () => {
    parkScreen();
  };

  /** Auto, once there is a catalog to choose from (#767); no catalog yet is no pick. */
  const autoPick = () => ((get() as any).perspectivesList?.length ? 'auto' : null);

  const patchLocal = (threadId: string, patch: Partial<ConversationThread>) => {
    const updatedAt = new Date().toISOString();
    const savedThreads = get().savedThreads.map((t) => (t.id === threadId ? { ...t, ...patch, updatedAt } : t));
    set({ savedThreads } as Partial<VoyagerState>);
  };

  /**
   * An optimistic metadata change: apply it on screen, send it, and if the
   * server refuses, put the previous values back — the list must never
   * show a state the server does not hold. `onRollback` lets a caller undo
   * what else the change did (closing the active thread cleared it).
   */
  const mutate = (
    threadId: string,
    action: string,
    patch: Partial<ConversationThread>,
    work: () => Promise<unknown>,
    onRollback?: () => void,
  ): Promise<boolean> => {
    const before = get().savedThreads.find((t) => t.id === threadId);
    if (!before) return Promise.resolve(false);
    const previous: Partial<ConversationThread> = { updatedAt: before.updatedAt };
    for (const key of Object.keys(patch) as Array<keyof ConversationThread>) (previous as any)[key] = before[key];
    patchLocal(threadId, patch);
    const gen = get().threadsGeneration;
    begin(threadId);
    // settled either way, and says whether the server took it: a caller that builds on the change waits for this
    return queued(threadId, work).then((row) => {
      settle(threadId);
      if (get().threadsGeneration === gen) applyServerStamp(threadId, row);
      return true;
    }, (err) => {
      settle(threadId);
      if (err instanceof StaleWorkspaceError) return false;
      warn(action, err);
      if (get().threadsGeneration !== gen) return false;
      const current = get().savedThreads.find((t) => t.id === threadId);
      if (!current) return false;
      // Undo only what THIS change set and nothing newer has touched since:
      // a rename A→B that fails after B→C was made must leave C standing.
      const restore: Partial<ConversationThread> = {};
      let untouched = true;
      for (const key of Object.keys(patch) as Array<keyof ConversationThread>) {
        if (current[key] === patch[key]) (restore as any)[key] = previous[key];
        else untouched = false;
      }
      if (untouched) restore.updatedAt = previous.updatedAt;
      if (Object.keys(restore).length === 0) return false;
      const savedThreads = get().savedThreads.map((t) => (t.id === threadId ? { ...t, ...restore } : t));
      set({ savedThreads } as Partial<VoyagerState>);
      if (untouched) onRollback?.();
      return false;
    });
  };

  /**
   * With the answer gate on, the list carries a conversation's questions only (#1270): a listed turn's answer
   * (`answerPending`) is rebuilt by the server for this reader when the thread is opened. That read, when it lands and
   * is still the newest read of the thread, rebuilds the thread's turns (`rebuildTurns`: the read's turns, then the
   * local ones it lacks — never a turn dropped, nothing merged into a turn) wherever the thread is — on screen, else
   * parked, and in the list — and its history with them. With the switch off nothing is pending and nothing is fetched.
   *
   * The hold (`threadAnswers[id] = 'loading'`) and the notice clear on ONE condition: a rebuild was applied (it
   * checks that no local turn or answer went backwards) and its turns have no pending turn — every turn carries its
   * answer, a terminal status, or is in flight here (its answer arrives on its socket into the kept turn). Otherwise
   * one more read, then `failed` (the composer's notice, and each pending turn's Try again) — never a silent success.
   * A read that timed out is read once more too; any other failed read is `failed` at once. Each read carries a per-thread token: a response that lands after a newer read of the same thread
   * was started (a reopen, a refresh) is discarded.
   */
  const answersToken = new Map<string, number>();
  let answersCounter = 0;
  const hasPending = (turns?: any[]) => (turns || []).some((t) => placeholder(t));
  const timedOut = (err: unknown) => ['ECONNABORTED', 'ETIMEDOUT'].includes((err as any)?.code);
  const liveTurns = (threadId: string): any[] | undefined => {
    const st = get() as any;
    if (st.currentThreadId === threadId) return st.landingAgentTurns;
    return get().parkedConversations[threadId]?.turns ?? get().savedThreads.find((t) => t.id === threadId)?.landingAgentTurns;
  };
  const loadAnswers = (threadId: string, attempt = 0) => {
    const gen = get().threadsGeneration;
    // a first read only for a thread with a listed turn to fill; a second one also for a turn not yet persisted
    if (attempt === 0 && !hasPending(liveTurns(threadId))) return;
    const token = ++answersCounter;
    answersToken.set(threadId, token);
    const current = () => get().threadsGeneration === gen && answersToken.get(threadId) === token;
    const mark = (state: 'loading' | 'failed' | null) => {
      const threadAnswers = { ...get().threadAnswers };
      if (state) threadAnswers[threadId] = state; else delete threadAnswers[threadId];
      set({ threadAnswers } as Partial<VoyagerState>);
    };
    const again = () => {
      // read once more, then the composer says the earlier answers are missing
      if (attempt < 1) loadAnswers(threadId, attempt + 1);
      else mark('failed');
    };
    mark('loading');
    getThread(threadId).then((row) => {
      if (!current()) return;      // a newer read of this thread (or another workspace) decides
      const fetched = threadRowToConversation(row).landingAgentTurns || [];
      const turns = rebuildTurns(liveTurns(threadId) || [], fetched);
      if (!turns) { again(); return; }       // the local turns stay exactly as they are
      const perspective = get().savedThreads.find((t) => t.id === threadId)?.perspectiveId ?? null;
      set({ savedThreads: get().savedThreads.map((t) => (t.id === threadId ? { ...t, landingAgentTurns: turns } : t)) } as Partial<VoyagerState>);
      const now = get() as any;
      const away = get().parkedConversations[threadId];
      if (now.currentThreadId === threadId) {
        set({ landingAgentTurns: turns, landingAgentHistory: restoredHistoryOf(turns).slice(-12),
          landingAgentThreadContext: restoredContextOf(turns, perspective) ?? now.landingAgentThreadContext } as Partial<VoyagerState>);
      } else if (away) {
        set({ parkedConversations: { ...get().parkedConversations, [threadId]: { ...away, turns,
          history: restoredHistoryOf(turns).slice(-12), threadContext: restoredContextOf(turns, perspective) ?? away.threadContext ?? null } } } as Partial<VoyagerState>);
      }
      if (!hasPending(turns)) mark(null);
      else again();
    }, (err) => {
      if (!current()) return;
      warn('load answers', err);
      // a long thread can outlast its timeout (#2188): read it once more
      if (timedOut(err)) { again(); return; }
      // the composer is not held forever by an outage: it says the earlier answers are missing, and the next open retries
      mark('failed');
    });
  };

  const replaceLocal = (thread: ConversationThread) => {
    const existing = get().savedThreads;
    const savedThreads = existing.some((t) => t.id === thread.id)
      ? existing.map((t) => (t.id === thread.id ? thread : t))
      : [thread, ...existing];
    set({ savedThreads } as Partial<VoyagerState>);
  };

  /**
   * Snapshots the server has not acknowledged wait in localStorage — the
   * same list threads from before the server store lived in. A persist that
   * fails (network, backend) parks its snapshot here; `migrateLegacy`
   * uploads whatever is waiting on the next fetch, so a reload after a
   * failed write does not lose the conversation.
   */
  const parkLocally = (thread: ConversationThread) => {
    const rest = loadLegacyThreads().filter((t) => t.id !== thread.id);
    persistLegacyThreads([thread, ...rest]);
  };
  const unparkLocally = (threadId: string) => {
    const legacy = loadLegacyThreads();
    if (legacy.some((t) => t.id === threadId)) persistLegacyThreads(legacy.filter((t) => t.id !== threadId));
  };

  /**
   * Threads that predate the server store, and snapshots parked by a failed
   * persist, live in localStorage. Each fetch carries them over, then
   * forgets the ones the server took, so nobody's history vanishes with the
   * switch or with one bad request. A row the server already has AND that
   * is at least as new wins; a failed upload leaves the local copy for the
   * next attempt.
   */
  const migrateLegacy = async (known: Map<string, ConversationThread>, gen: number) => {
    const legacy = loadLegacyThreads();
    if (legacy.length === 0) return;
    // What the server took, by id and the version it took. Only those
    // entries are forgotten — from a fresh read, so a snapshot parked by a
    // failed persist while these uploads ran is not thrown away with them.
    const taken = new Map<string, string>();
    const failed: ConversationThread[] = [];
    const localAtStart = new Map(get().savedThreads.map(thread => [thread.id, thread]));
    const defer = (cached: ConversationThread) => {
      const current = get().savedThreads.find(thread => thread.id === cached.id);
      const pending = loadLegacyThreads().find(thread => thread.id === cached.id);
      if (!current && !pending) return;
      const latest = current && current.landingAgentTurns !== localAtStart.get(cached.id)?.landingAgentTurns
        ? current : pending || current || cached;
      const turns = [...(latest.landingAgentTurns || [])];
      const owner = turns.find(turn => turn.foreground) || turns[turns.length - 1];
      let merged = false;
      for (const turn of cached.landingAgentTurns || []) {
        if (!turn.diagnosisOperationId && !turn.analysisOperationId) continue;
        const index = turns.findIndex(saved => saved.id === turn.id);
        const saved = turns[index];
        if (!saved) { turns.push({ ...turn, foreground: false }); merged = true; }
        else if (turn.diagnosisOperationId !== saved.diagnosisOperationId
          && (turn.diagnosisPreviousOperationId === saved.diagnosisOperationId
            || (turn.diagnosisPreviousOperationIds || []).includes(saved.diagnosisOperationId || null))) {
          turns[index] = turn;
          merged = true;
        }
      }
      const preserved = { ...latest, landingAgentTurns: merged
        ? turns.map(turn => ({ ...turn, foreground: turn.id === owner?.id })) : latest.landingAgentTurns };
      parkLocally(preserved);
      failed.push(preserved);
    };
    for (let t of legacy) {
      if (get().threadsGeneration !== gen) break;
      const cached = t;
      const pending = loadLegacyThreads().find(thread => thread.id === t.id);
      const current = get().savedThreads.find(thread => thread.id === t.id);
      if ((isInFlight(t.id) && current?.landingAgentTurns !== known.get(t.id)?.landingAgentTurns)
        || current !== localAtStart.get(t.id) || (pending && JSON.stringify(pending) !== JSON.stringify(cached))) {
        defer(cached);
        continue;
      }
      if (!pending) continue;
      let server = known.get(t.id);
      if (server && hasPending(server.landingAgentTurns)) {
        // the list carried this thread's questions only (#1596): recover against its whole read, never a placeholder
        try {
          server = threadRowToConversation(await getThread(t.id));
        } catch {
          defer(cached);
          continue;
        }
        if (get().threadsGeneration !== gen) break;
        // the read took time: a delete meanwhile took the parked copy with it (`unparkLocally`) and is final; an edit
        // meanwhile is deferred exactly as the checks above defer it — neither is recovered over
        const after = loadLegacyThreads().find(thread => thread.id === t.id);
        if (!after) continue;
        const now = get().savedThreads.find(thread => thread.id === t.id);
        if ((isInFlight(t.id) && now?.landingAgentTurns !== known.get(t.id)?.landingAgentTurns)
          || now !== current || JSON.stringify(after) !== JSON.stringify(cached)) {
          defer(cached);
          continue;
        }
      }
      // The server has this id: upload only a parked copy that extends the
      // revision the server still holds. A copy from before the server store
      // (no base) or built on a revision the server has since moved past
      // must not overwrite what is there — and no clock is consulted.
      if (server && (!t.baseUpdatedAt || t.baseUpdatedAt !== server.updatedAt)) {
        const turns = [...(server.landingAgentTurns || [])];
        const recoveredIds = new Set<string>();
        let recovered = false;
        for (const pending of t.landingAgentTurns || []) {
          if (!pending.diagnosisOperationId && !pending.analysisOperationId) continue;
          const index = turns.findIndex(turn => turn.id === pending.id);
          const saved = turns[index];
          if (pending.analysisOperationId) {
            if (!saved) { turns.push(pending); recoveredIds.add(pending.id); recovered = true; }
            else if (saved.analysisOperationId === pending.analysisOperationId && pending.analysisCancelPending && !saved.analysisCancelPending) {
              turns[index] = { ...saved, status: 'cancelled', stopped: true, analysisCancelPending: true, analysisDisconnected: false };
              recoveredIds.add(pending.id); recovered = true;
            } else if (saved.analysisOperationId === pending.analysisOperationId && pending.analysisAwaitingLadder
              && !saved.result?.ladder && !saved.stopped && !['error', 'cancelled'].includes(saved.status)) {
              turns[index] = { ...saved, analysisAwaitingLadder: true, analysisDisconnected: false };
              recoveredIds.add(pending.id); recovered = true;
            }
            continue;
          }
          const predecessor = saved && (saved.diagnosisOperationId === pending.diagnosisPreviousOperationId
            || (pending.diagnosisPreviousOperationIds || []).includes(saved.diagnosisOperationId || null));
          if (!saved || (saved.diagnosisOperationId !== pending.diagnosisOperationId && predecessor)) {
            const recoveredTurn = { ...pending, result: null, ...(pending.stopped ? {} : { status: 'running' as const, error: null, diagnosisDisconnected: false }) };
            if (index < 0) turns.push(recoveredTurn); else turns[index] = recoveredTurn;
            recoveredIds.add(pending.id);
            recovered = true;
          } else if (saved.diagnosisOperationId === pending.diagnosisOperationId && pending.diagnosisCancelPending && !saved.diagnosisCancelPending) {
            turns[index] = { ...saved, result: null, status: 'cancelled', stopped: true, diagnosisCancelPending: true };
            recoveredIds.add(pending.id);
            recovered = true;
          }
        }
        if (!recovered) { taken.set(t.id, JSON.stringify(cached)); continue; }
        const serverTurns = server.landingAgentTurns || [];
        const cachedTurns = t.landingAgentTurns || [];
        const serverOwner = serverTurns.find(turn => turn.foreground) || serverTurns[serverTurns.length - 1];
        const recoveredOwner = cachedTurns.find(turn => turn.foreground && recoveredIds.has(turn.id));
        const superseded = cachedTurns.find(turn => turn.id === serverOwner?.id && turn.foreground === false
          && turn.diagnosisOperationId === serverOwner?.diagnosisOperationId);
        let owner = serverOwner?.id || turns[turns.length - 1]?.id;
        if (recoveredOwner && (!serverOwner || recoveredOwner.id === serverOwner.id || superseded)) owner = recoveredOwner.id;
        else if (![...serverTurns, ...cachedTurns].some(turn => typeof turn.foreground === 'boolean')) {
          const legacyOwner = cachedTurns[cachedTurns.length - 1];
          if (legacyOwner && recoveredIds.has(legacyOwner.id)
            && (!serverOwner || legacyOwner.id === serverOwner.id || cachedTurns.some(turn =>
              turn.id === serverOwner.id && turn.diagnosisOperationId === serverOwner.diagnosisOperationId))) owner = legacyOwner.id;
        }
        const mergedTurns = turns.map(turn => ({ ...turn, foreground: turn.id === owner }));
        t = { ...server, updatedAt: t.updatedAt, baseUpdatedAt: server.updatedAt, landingAgentTurns: mergedTurns };
        replaceLocal(t);
        if (get().currentThreadId === t.id) {
          set({ landingAgentTurns: mergedTurns, landingAgentHistory: restoredHistoryOf(mergedTurns).slice(-12) } as Partial<VoyagerState>);
          loadAnswers(t.id);     // the server's turns came from the list: questions only with the gate on (#1270)
        }
      }
      if (get().threadsGeneration !== gen) break;
      const beforeUpload = get().savedThreads.find(s => s.id === t.id);
      const diagnosisRecovery = (t.landingAgentTurns || []).some(turn => (turn.diagnosisOperationId || turn.analysisOperationId) && (turn.status === 'running' || turn.diagnosisCancelPending || turn.analysisCancelPending || turn.analysisAwaitingLadder || turn.diagnosisDisconnected || turn.analysisDisconnected || !(server?.landingAgentTurns || []).some(saved => saved.id === turn.id && saved.diagnosisOperationId === turn.diagnosisOperationId)));
      try {
        // On the thread's own write queue, marked in flight: a persist the
        // person makes meanwhile lands after this, never under it.
        begin(t.id);
        let row: ThreadRow | null;
        let deferred = false;
        try {
          row = await queued(t.id, async () => {
            // A newer write of ours settled while this waited its turn — the
            // server already has more than this local copy. Skip it.
            const current = get().savedThreads.find((s) => s.id === t.id);
            const pending = loadLegacyThreads().find(thread => thread.id === t.id);
            if (current?.landingAgentTurns !== beforeUpload?.landingAgentTurns || JSON.stringify(pending) !== JSON.stringify(cached)) {
              defer(cached);
              deferred = true;
              return null;
            }
            if (!diagnosisRecovery && current && current !== t && t.baseUpdatedAt && current.updatedAt !== t.baseUpdatedAt) return null;
            return upsertThread(t.id, {
              ...(server && diagnosisRecovery ? {} : {
                title: t.title,
                preview: t.preview,
                perspective_id: t.perspectiveId ?? t.detectedPerspective ?? null,
                domain_id: t.domainId ?? t.detectedDomain ?? null,
                space_id: t.spaceId ?? null,
              }),
              // the objective it was asked inside (#766), on every write; the server re-checks only a change
              ...(t.objectiveId ? { objective_id: t.objectiveId } : {}),
              snapshot: { turns: t.landingAgentTurns || [] },
            });
          });
        } finally {
          settle(t.id);
        }
        if (deferred) continue;
        taken.set(t.id, JSON.stringify(cached));
        if (get().threadsGeneration !== gen) break;
        if (row) {
          const fromServer = threadRowToConversation(row);
          const current = get().savedThreads.find((s) => s.id === t.id);
          if (!current && (beforeUpload || !loadLegacyThreads().some(pending => pending.id === t.id))) continue;
          // A thread already on screen may carry edits queued behind this
          // upload (a star, a rename, another turn): keep them, take only
          // what the server decides — the timestamps.
          const turnsChanged = current && current.landingAgentTurns !== beforeUpload?.landingAgentTurns;
          replaceLocal(current
            ? { ...current, createdAt: fromServer.createdAt, updatedAt: fromServer.updatedAt,
                lastOpenedAt: current.lastOpenedAt || fromServer.lastOpenedAt,
                ...(diagnosisRecovery && !turnsChanged ? { landingAgentTurns: fromServer.landingAgentTurns } : {}) }
            : fromServer);
          if (diagnosisRecovery && !turnsChanged && get().currentThreadId === t.id) {
            set({ landingAgentTurns: fromServer.landingAgentTurns || [], landingAgentHistory: restoredHistoryOf(fromServer.landingAgentTurns || []).slice(-12) } as Partial<VoyagerState>);
          }
        }
      } catch (err) {
        if (err instanceof StaleWorkspaceError) break;
        if ((err as any)?.response?.status === 410) { taken.set(t.id, JSON.stringify(cached)); continue; }
        warn('migrate', err);
        failed.push(t);
      }
    }
    if (taken.size > 0) {
      persistLegacyThreads(loadLegacyThreads().filter((t) => taken.get(t.id) !== JSON.stringify(t)));
    }
    if (failed.length > 0 && get().threadsGeneration === gen) {
      // Still this person's history: show it from the local copy, and try
      // the upload again later rather than hiding it for the session.
      for (const t of failed) if (loadLegacyThreads().some(pending => pending.id === t.id)
        && !get().savedThreads.some((s) => s.id === t.id)) replaceLocal(t);
      migrateRetry = Math.min(migrateRetry ? migrateRetry * 2 : 5000, 60_000);
      setTimeout(() => {
        if (get().threadsGeneration !== gen) return;
        get().fetchThreads();
      }, migrateRetry);
    } else {
      migrateRetry = 0;
    }
  };

  /** Park a chat's snapshot durably (#202) under the namespace it was queued in; a reload there replays it. */
  const parkAgentChat = (threadId: string, entry: { turns: any[]; ns: string | null; title: string; source: { kind: string; id: string | null }; kind?: ChatKind }) => {
    parkAgentChatSnapshot({ threadId, kind: entry.kind ?? 'agent_chat', title: entry.title, source: entry.source, turns: entry.turns, at: new Date().toISOString() }, entry.ns);
  };
  const unparkAgentChat = (threadId: string, ns?: string | null) => unparkAgentChatSnapshot(threadId, ns);

  /**
   * Snapshots parked by a failed flush go out again once the list has
   * loaded: the thread is put back if the server no longer lists it (the
   * upsert creates it), the parked turns are joined with whatever the
   * server has by id, and the outbox takes it from there — success unparks.
   */
  const replayParkedAgentChats = () => {
    for (const p of loadParkedAgentChats()) {
      if (!isChatKind(p.kind) || !Array.isArray(p.turns)) { unparkAgentChat(p.threadId); continue; }
      let thread = get().savedThreads.find((t) => t.id === p.threadId);
      if (!thread) {
        const now = p.at || new Date().toISOString();
        thread = {
          id: p.threadId, title: p.title || 'Chat', preview: '', messages: [], createdAt: now, updatedAt: now, spaceId: null,
          detectedPerspective: null, detectedDomain: null, landingAgentTurns: [], perspectiveId: null, domainId: null,
          kind: p.kind as ChatKind, starred: false, closedAt: null, lastOpenedAt: now, tabOrder: 0,
          sourceKind: p.source?.kind ?? 'agent', sourceId: p.source?.id ?? null, doc: null,
        };
        replaceLocal(thread);
      }
      if (!isChatKind(thread.kind)) { unparkAgentChat(p.threadId); continue; }
      const seen = new Set(p.turns.map((t: any) => t?.id));
      const turns = [...p.turns, ...(thread.landingAgentTurns || []).filter((t: any) => !seen.has(t?.id))];
      get().persistAgentChat(p.threadId, turns);
    }
  };

  const setChatUnsaved = (threadId: string, unsaved: boolean) => {
    const next: Record<string, true> = { ...get().agentChatUnsaved };
    if (unsaved) next[threadId] = true; else delete next[threadId];
    set({ agentChatUnsaved: next } as Partial<VoyagerState>);
  };

  /**
   * Send the outbox's snapshot for a chat (#202) on the thread's write
   * queue. Success clears the unsaved mark only if that snapshot is still
   * the latest — a newer one queued behind clears it when it lands. Failure
   * marks the chat unsaved and tries again with backoff; after the last
   * backoff the snapshot waits for the next turn, which writes it all.
   */
  const flushAgentChat = (threadId: string) => {
    const entry = chatOutbox.get(threadId);
    if (!entry) return;
    const existing = get().savedThreads.find((t) => t.id === threadId);
    if (!existing || !isChatKind(existing.kind)) { chatOutbox.delete(threadId); return; }
    const turns = entry.turns;
    const last = [...turns].reverse().find((t) => t?.role === 'agent');
    const gen = get().threadsGeneration;
    // What this write knows about where it belongs, kept for after a switch.
    const parkable = { turns, ns: entry.ns, title: entry.title, source: entry.source, kind: existing.kind as ChatKind };
    begin(threadId);
    // The kind and the source ride every write: the chat's creation and
    // its first turn can reach the server in either order, and whichever
    // lands first must not leave a plain thread behind.
    queued(threadId, () => upsertThread(threadId, {
      kind: existing.kind as ChatKind,
      source_kind: existing.sourceKind ?? 'agent',
      source_id: existing.sourceId ?? null,
      preview: String(last?.text || '').slice(0, 240),
      snapshot: { turns },
    })).then((row) => {
      settle(threadId);
      // The server took it after the workspace changed: nothing to show
      // here, but the old workspace's park no longer owes these turns —
      // only these: a newer snapshot parked by the switch, whose own write
      // never started, keeps what it added until that workspace sends it.
      if (get().threadsGeneration !== gen) { confirmParkedAgentChatTurns(threadId, turns, parkable.ns); return; }
      applyServerStamp(threadId, row);
      // The server unions turns by id (the same chat open in two windows):
      // its row is the whole transcript. Taken only if nothing here changed
      // since this write went out — a newer snapshot brings its own merge.
      const merged = (row as ThreadRow | undefined)?.snapshot?.turns;
      const current = get().savedThreads.find((t) => t.id === threadId);
      if (Array.isArray(merged) && current && current.landingAgentTurns === turns) {
        // the server keeps a composed answer by reference (#296): the words this viewer was shown on THIS read stay
        // on screen — a redacted copy of a turn that is live here never replaces it
        const live = new Map((turns as any[]).map((t) => [t?.id, t]));
        // …and a group turn's nested reply (#307) the same way: the words shown on this read stay
        const hasWords = (t: any) => !!(t?.text || t?.reply?.text);
        const kept = merged.map((m: any) => ((m?.redacted || m?.reply?.redacted) && hasWords(live.get(m.id)) ? live.get(m.id) : m));
        set({ savedThreads: get().savedThreads.map((t) => (t.id === threadId ? { ...t, landingAgentTurns: kept } : t)) } as Partial<VoyagerState>);
      }
      const now = chatOutbox.get(threadId);
      if (now && now.turns === turns) { chatOutbox.delete(threadId); setChatUnsaved(threadId, false); }
      confirmParkedAgentChatTurns(threadId, turns, parkable.ns);
    }, (err) => {
      settle(threadId);
      // Never started (the switch came first): the reset parked it already.
      if (err instanceof StaleWorkspaceError) { chatOutbox.delete(threadId); return; }
      warn('persist agent chat', err);
      // Failed after the workspace changed: no retry here, but the snapshot
      // is parked where it belongs, so that workspace's next fetch sends it.
      if (get().threadsGeneration !== gen) { parkAgentChat(threadId, parkable); return; }
      if ((err as any)?.response?.status === 410) {
        // Deleted meanwhile (another tab): it stays deleted here too.
        chatOutbox.delete(threadId);
        setChatUnsaved(threadId, false);
        unparkAgentChat(threadId);
        set({ savedThreads: get().savedThreads.filter((t) => t.id !== threadId),
          ...(get().currentThreadId === threadId ? { currentThreadId: null } : {}) } as Partial<VoyagerState>);
        return;
      }
      const now = chatOutbox.get(threadId);
      if (!now || now.turns !== turns) return; // a newer snapshot is on its way and decides
      setChatUnsaved(threadId, true);
      // Durably, too: the server may already have acted on this line, and a
      // reload before the retries land must not lose it.
      parkAgentChat(threadId, parkable);
      const delay = CHAT_RETRY_MS[now.attempt];
      if (delay == null) return; // out of backoff: the next turn carries it
      now.attempt += 1;
      now.timer = setTimeout(() => {
        now.timer = null;
        if (get().threadsGeneration === gen) flushAgentChat(threadId);
      }, delay);
    });
  };

  return {
    savedThreads: [],
    currentThreadId: null,
    threadsLoaded: false,
    threadsError: null,
    threadAnswers: {},
    threadsGeneration: 0,
    pendingThreadOpen: null,
    agentChatUnsaved: {},
    pendingThreadObjective: null,

    openThreadWhenLoaded: (threadId: string) => {
      if (get().savedThreads.some((t) => t.id === threadId)) {
        get().loadThread(threadId);
        return;
      }
      set({ pendingThreadOpen: threadId } as Partial<VoyagerState>);
    },

    fetchThreads: async () => {
      const gen = get().threadsGeneration;
      fetchAttempt += 1;
      const attempt = fetchAttempt;
      try {
        // Walk every page by keyset cursor: a thread past the first page is
        // still a thread, and a row that changes between pages moves to the
        // front (already read) instead of shifting the pages under us.
        const rows: ThreadRow[] = [];
        const ids = new Set<string>();
        const take = (page: ThreadRow[]) => {
          for (const r of page) if (!ids.has(r.id)) { ids.add(r.id); rows.push(r); }
        };
        let before: string | undefined;
        let pages = 0;
        for (;;) {
          const page = await listThreads('all', THREADS_PAGE, before);
          // A switch happened while this was in flight: these rows belong to
          // the workspace we just left.
          if (get().threadsGeneration !== gen) return;
          pages += 1;
          take(page);
          if (page.length < THREADS_PAGE) break;
          before = threadCursor(page[page.length - 1]);
        }
        if (pages > 1) {
          // A row another tab changed mid-walk moved ahead of the cursor
          // without ever being read. Whatever moved is now at the front, so
          // one more look at the first page picks it up.
          const head = await listThreads('all', THREADS_PAGE);
          if (get().threadsGeneration !== gen) return;
          take(head);
        }
        // Merge, don't replace: a thread with a change of ours still in
        // flight (an answer that finished before the startup fetch returned,
        // a star the server has not answered) is newer than anything the
        // server sent. Everything settled takes the server's word.
        const local = new Map(get().savedThreads.map((t) => [t.id, t]));
        const fromServer = rows.map(threadRowToConversation);
        const savedThreads = fromServer.map((t) => {
          const mine = local.get(t.id);
          return mine && isInFlight(t.id) ? mine : t;
        });
        // A local thread the server did not return is kept only while its
        // own write is in flight. Anything settled and absent was deleted
        // elsewhere — another tab, a client — and must go.
        const seen = new Set(savedThreads.map((t) => t.id));
        for (const t of get().savedThreads) {
          if (!seen.has(t.id) && isInFlight(t.id)) savedThreads.unshift(t);
        }
        set({ savedThreads, threadsLoaded: true, threadsError: null } as Partial<VoyagerState>);
        retryDelay = 0;
        const pending = get().pendingThreadOpen;
        if (pending && savedThreads.some((t) => t.id === pending)) {
          set({ pendingThreadOpen: null } as Partial<VoyagerState>);
          get().loadThread(pending);
        }
        replayParkedAgentChats();
        await migrateLegacy(new Map(fromServer.map((t) => [t.id, t])), gen);
      } catch (err) {
        warn('fetch', err);
        if (get().threadsGeneration !== gen) return;
        set({ threadsError: (err as any)?.message || 'Could not load your threads' } as Partial<VoyagerState>);
        // Offline, or the API is down: show what this browser still holds —
        // the localStorage-era list and any snapshot a failed persist parked
        // — so History is not blank for the outage. Nothing is marked in
        // flight, so the first successful fetch takes the server's word and
        // the migration re-uploads what the server does not have.
        if (!get().threadsLoaded && get().savedThreads.length === 0) {
          const local = loadLegacyThreads();
          if (local.length) set({ savedThreads: local } as Partial<VoyagerState>);
        }
        // A transient failure must not blank the history for the whole
        // session: try again, backing off up to 30s, until one succeeds or
        // the workspace changes.
        retryDelay = Math.min(retryDelay ? retryDelay * 2 : 1000, 30_000);
        setTimeout(() => {
          if (get().threadsGeneration === gen && (!get().threadsLoaded || loadLegacyThreads().length > 0) && fetchAttempt === attempt) {
            get().fetchThreads();
          }
        }, retryDelay);
      }
    },

    resetThreadsWorkspace: () => {
      // Bump the generation FIRST so nothing already queued can run under
      // the new identity, then end the conversation.
      // A chat snapshot waiting in the outbox belongs to the old workspace:
      // parked there — in flight or not — before it is forgotten here, so
      // a write that fails after this cannot lose it.
      chatOutbox.forEach((entry, threadId) => {
        if (entry.timer) clearTimeout(entry.timer);
        parkAgentChat(threadId, entry);
      });
      chatOutbox.clear();
      set({
        savedThreads: [],
        threadsLoaded: false,
        threadsError: null,
        threadAnswers: {},
        threadsGeneration: get().threadsGeneration + 1,
        agentChatUnsaved: {},
        // an objective's space (#765) belongs to the workspace it was opened in
        objectiveSpaceId: null,
      } as Partial<VoyagerState>);
      // Parked runs are discarded, not settled and stored: a store now
      // would upload the previous workspace's transcript under this one.
      get().discardParkedConversations();
      // Unsent text belongs to the workspace it was typed in.
      clearDrafts();
      // Pinned numbers are per account: read the new namespace's.
      set({ pins: loadPins() } as Partial<VoyagerState>);
      endConversation(false);
    },

    persistCurrentThread: (forThreadId?: string) => {
      const state = get() as any;
      const onScreen = !forThreadId || forThreadId === state.currentThreadId;
      const parked = !onScreen ? get().parkedConversations[forThreadId as string] : null;
      if (!onScreen && !parked) return; // closed or deleted meanwhile
      // a reading card (#1174 P3b) is the asker's live card, rebuilt by the server on a reopen: never on the snapshot
      // nor a turn's streamed words (C8, #1672): live only — a Stop keeps what they showed as `stoppedWords`
      // nor F4's live progress frames and "away" mark (#1858): live only, in memory only
      // the working copy stays rendered here (#1858 round 2): the stored form (`storedTurn`: references only, no
      // undecided row or SQL) is applied only at the sinks, `upsertThread` and the localStorage buffer
      const turns = ((onScreen ? state.landingAgentTurns : parked?.turns) || []).map(withoutReadingCard)
        .map(withoutStreaming).map(withoutLiveProgress);
      if (turns.length === 0) return;
      const savedThreads = state.savedThreads as ConversationThread[];
      const currentThreadId = (onScreen ? state.currentThreadId : forThreadId) as string | null;
      // A background persist stores INTO a thread that is still listed; it
      // never re-creates one that was deleted meanwhile.
      if (!onScreen && !savedThreads.some((t) => t.id === forThreadId)) return;
      const firstUser = turns[0]?.userText || 'Conversation';
      const lastTurn = turns[turns.length - 1];
      const res = lastTurn?.result || {};
      const preview = (res.synthesis?.headline || res.formatterTitle || res.formatterSummary || '').toString().slice(0, 240);
      const now = new Date().toISOString();
      const existing = currentThreadId ? savedThreads.find((t) => t.id === currentThreadId) : null;
      // A review document never carries a transcript (#189): its content is
      // server-managed. Turns that reach here under a doc's id belong to a
      // question that should have opened its own thread — not written onto the review.
      // An agent chat (#202) keeps its own transcript (`persistAgentChat`): landing turns never land on it.
      if (existing?.kind === 'doc' || isChatKind(existing?.kind)) return;
      const id = existing?.id || currentThreadId || generateId();
      // The screen's perspective belongs to the tab on screen. A background
      // thread whose answer just landed keeps its own.
      const perspectiveId = onScreen ? (state.selectedPerspectiveId || null) : (existing?.perspectiveId ?? null);
      const domainId = onScreen ? (state.selectedLandingAgentDomainId || null) : (existing?.domainId ?? null);
      // What a NEW thread was opened from (#189: a review section's Why),
      // read once — a later persist must not stamp it on another thread.
      const source = !existing ? (state.pendingThreadSource as { kind: string; id: string } | null) : null;
      if (source) set({ pendingThreadSource: null } as Partial<VoyagerState>);
      // the objective a NEW thread is asked inside (#766), read once like its source
      const objective = existing ? (existing.objectiveId ?? null) : (onScreen ? (state.pendingThreadObjective ?? null) : null);
      if (!existing && objective) set({ pendingThreadObjective: null } as Partial<VoyagerState>);
      const snapshot: ConversationThread = {
        id,
        title: existing?.title || firstUser.slice(0, 80),
        preview,
        messages: [],
        createdAt: existing?.createdAt || now,
        updatedAt: now,
        spaceId: existing?.spaceId ?? null,
        detectedPerspective: perspectiveId,
        detectedDomain: domainId,
        landingAgentTurns: turns,
        perspectiveId,
        domainId,
        kind: existing?.kind || 'thread',
        starred: existing?.starred || false,
        // The tab on screen is open by definition; a background thread keeps
        // whatever state it has (it may have just been closed).
        closedAt: onScreen ? null : (existing?.closedAt ?? null),
        lastOpenedAt: existing?.lastOpenedAt || now,
        tabOrder: existing?.tabOrder || 0,
        sourceKind: existing?.sourceKind ?? source?.kind ?? null,
        sourceId: existing?.sourceId ?? source?.id ?? null,
        objectiveId: objective,
        // The server revision this copy extends; a parked retry compares it.
        baseUpdatedAt: existing?.baseUpdatedAt ?? existing?.updatedAt ?? null,
      };
      if (turns.some((t: any) => (t.diagnosisOperationId || t.analysisOperationId))) parkLocally(snapshot);
      replaceLocal(snapshot);
      if (onScreen) set({ currentThreadId: id } as Partial<VoyagerState>);
      const gen = get().threadsGeneration;
      begin(id);
      // The server seeds the title from the first question itself; sending
      // one only for a NEW thread keeps a later persist from undoing a rename.
      queued(id, () => upsertThread(id, {
        ...(existing ? {} : { title: snapshot.title }),
        ...(source ? { source_kind: source.kind, source_id: source.id } : {}),
        // the thread's objective (#766) rides every persist, so a creating write that failed cannot lose it: the server
        // re-checks only a change
        ...(objective ? { objective_id: objective } : {}),
        preview,
        perspective_id: perspectiveId,
        domain_id: domainId,
        snapshot: { turns },
      }))
        .then((row) => {
          settle(id);
          // Started under a workspace we have since left: drop it.
          if (get().threadsGeneration !== gen) return;
          const fromServer = threadRowToConversation(row);
          const current = get().savedThreads.find((t) => t.id === id);
          if (!current) return; // deleted meanwhile
          const pending = loadLegacyThreads().find(t => t.id === id);
          if (!pending || !(pending.landingAgentTurns || []).some(t => t.diagnosisOperationId || t.analysisOperationId)
            || JSON.stringify(redactClaimWords(pending.landingAgentTurns)) === JSON.stringify(redactClaimWords(storedTurns(snapshot.landingAgentTurns)))) unparkLocally(id);
          else parkLocally({ ...pending, baseUpdatedAt: fromServer.updatedAt });
          // Take only what the server decides — timestamps, and the title when
          // it seeded one. Local turns and metadata (a star, a rename, a close
          // made while this was in flight) are newer than this response.
          replaceLocal({
            ...current,
            createdAt: fromServer.createdAt,
            updatedAt: fromServer.updatedAt,
            baseUpdatedAt: fromServer.updatedAt,
            lastOpenedAt: current.lastOpenedAt || fromServer.lastOpenedAt,
            title: current.title || fromServer.title,
          });
        })
        .catch((err) => {
          settle(id);
          if (err instanceof StaleWorkspaceError) return;
          if ((err as any)?.response?.status === 410) {
            // Deleted meanwhile (another tab): it stays deleted here too.
            set({ savedThreads: get().savedThreads.filter((t) => t.id !== id) } as Partial<VoyagerState>);
            if (get().currentThreadId === id) endConversation();
            unparkLocally(id);
            return;
          }
          warn('persist', err);
          // Keep the transcript somewhere a reload can find it; the next
          // fetch retries the upload. Not for a thread deleted meanwhile —
          // a delete is final, and must not come back as a retry.
          if (get().threadsGeneration === gen) {
            const latest = get().savedThreads.find(t => t.id === id);
            if (latest) parkLocally((latest.landingAgentTurns || []).some(t => t.diagnosisOperationId || t.analysisOperationId) ? latest : snapshot);
          }
        });
    },

    clearLandingChat: () => {
      // "New analysis" / Home: the thread you leave keeps running in its tab.
      leaveScreen();
      // A new chat starts on Auto (#767): a pick belongs to the thread it was made in — and outside any objective (#766).
      set({ selectedPerspectiveId: autoPick(), pendingThreadObjective: null } as Partial<VoyagerState>);
    },

    presetObjectivePick: (perspectiveId) => {
      // the objective's own perspective (#749 "from objective"); Auto when it names none the asker has
      if (perspectiveId && (get() as any).perspectivesList?.some((p: any) => p.id === perspectiveId)) get().pickPerspective(perspectiveId);
      else set({ selectedPerspectiveId: autoPick() } as Partial<VoyagerState>);
    },

    askInObjective: (objectiveId, text) => {
      // the space shows no conversation (openObjectiveSpace parked it): the question starts a new thread, in the pick
      // the space's composer shows — preset from the objective, or the asker's own change to it
      set({ pendingThreadObjective: objectiveId, objectiveSpaceId: null, currentView: 'landing' } as Partial<VoyagerState>);
      const started = get().sendLandingAgentQuery(text);
      if (!started) set({ pendingThreadObjective: null, objectiveSpaceId: objectiveId } as Partial<VoyagerState>);
      return started;
    },

    tieThreadToObjective: async (threadId, objectiveId) => {
      const gen = get().threadsGeneration;
      try {
        await queued(threadId, () => patchThread(threadId, { objective_id: objectiveId }));
      } catch (err) {
        warn('tie to objective', err);
        return false;
      }
      if (get().threadsGeneration !== gen) return false;
      if (get().savedThreads.some((t) => t.id === threadId)) patchLocal(threadId, { objectiveId });
      return true;
    },

    loadThread: (threadId: string) => {
      const state = get() as any;
      const thread = (state.savedThreads as ConversationThread[]).find((t) => t.id === threadId);
      if (!thread) return;

      if (state.currentThreadId === threadId) {
        // Already the conversation on screen — picked again from History
        // after a trip through the rail. Come back to it; and if a refresh
        // meanwhile marked it closed, reopen it.
        set({ currentView: 'landing' } as any);
        if (!isOpenThread(thread)) {
          mutate(threadId, 'reopen', { closedAt: null, lastOpenedAt: new Date().toISOString() },
            () => apiReopenThread(threadId));
        }
        loadAnswers(threadId);
        return;
      }
      leaveScreen();
      // a `?turn=` anchor belongs to the thread it was opened with (#472): opening another drops it; so does a new
      // chat's pending objective (#766): the thread opened carries its own
      set({ threadTurnAnchor: null, pendingThreadObjective: null } as any);

      const now = new Date().toISOString();
      const wasClosed = !isOpenThread(thread);
      if (thread.kind === 'doc' || isChatKind(thread.kind)) {
        // A review document (#189) has no conversation to restore: the tab
        // shows the doc, and its numbers are the server's. An agent chat
        // (#202) paints from its own snapshot the same way.
        set({
          currentView: 'landing',
          currentThreadId: thread.id,
          ...(thread.perspectiveId ? { selectedPerspectiveId: thread.perspectiveId } : {}),
        } as any);
        const open = () => (wasClosed ? apiReopenThread(threadId) : touchThread(threadId));
        mutate(threadId, wasClosed ? 'reopen' : 'touch', { closedAt: null, lastOpenedAt: now },
          !isChatKind(thread.kind) ? open : () => open().catch((err) => {
            // A chat this browser still lists but the server no longer has
            // (an earlier run that never created it, a delete elsewhere):
            // the transcript on screen is the truth, so make it exist again.
            if ((err as any)?.response?.status !== 404) throw err;
            return upsertThread(threadId, {
              kind: thread.kind as ChatKind, title: thread.title, source_kind: thread.sourceKind ?? 'agent', source_id: thread.sourceId ?? null,
              preview: thread.preview || '', snapshot: { turns: thread.landingAgentTurns || [] },
            });
          }));
        return;
      }
      if (get().unparkConversation(threadId)) {
        // Its live state was waiting: turns, socket, an answer that landed
        // while another tab was up. Nothing to rebuild, nothing to fetch.
        set({
          currentView: 'landing',
          currentThreadId: thread.id,
          // The thread's own pick (#767); one that recorded none is on Auto, never the last thread's pick.
          // A domain of null is "All domains" and IS restored.
          selectedPerspectiveId: thread.perspectiveId || autoPick(),
          selectedLandingAgentDomainId: thread.domainId ?? null,
        } as any);
        mutate(threadId, wasClosed ? 'reopen' : 'touch', { closedAt: null, lastOpenedAt: now },
          () => (wasClosed ? apiReopenThread(threadId) : touchThread(threadId)));
        loadAnswers(threadId);
        return;
      }

      const restoredTurns = thread.landingAgentTurns || [];
      // Each entry keeps its turn id, so a late reply after a reload still
      // lands under ITS question (never appended at the end).
      const restoredHistory = restoredHistoryOf(restoredTurns);
      // The thread's context survives a reload: the scope the last answer
      // wrote (a ladder's thread_context, or a walk's target).
      const restoredContext = restoredContextOf(restoredTurns, thread.perspectiveId ?? null);

      set({
        currentView: 'landing',
        currentThreadId: thread.id,
        landingAgentTurns: restoredTurns,
        landingAgentMessages: [],
        landingAgentHistory: restoredHistory.slice(-12),
        landingAgentThreadContext: restoredContext,
        landingAgentEvents: [],
        landingAgentStatus: 'idle',
        landingAgentResult: null,
        landingAgentClarification: null,
        landingAgentError: null,
        landingAgentWs: null,
        selectedPerspectiveId: thread.perspectiveId || autoPick(),
        selectedLandingAgentDomainId: thread.domainId ?? null,
      } as any);

      // Reopening from History is an open too, so one call covers both. A
      // refused reopen puts the thread back in History; the conversation on
      // screen is the snapshot and stays readable either way.
      mutate(threadId, wasClosed ? 'reopen' : 'touch', { closedAt: null, lastOpenedAt: now },
        () => (wasClosed ? apiReopenThread(threadId) : touchThread(threadId)));
      // a turn listed without its answer (#1270) is filled from the thread's own read
      loadAnswers(threadId);
    },

    renameThread: (threadId: string, title: string) => {
      // Same normalisation as the server, so what is shown is what is kept.
      const clean = title.trim().slice(0, TITLE_MAX);
      if (!clean) return Promise.resolve(false);
      return mutate(threadId, 'rename', { title: clean }, () => patchThread(threadId, { title: clean }));
    },

    starThread: (threadId: string, starred: boolean) => {
      mutate(threadId, 'star', { starred }, () => patchThread(threadId, { starred }));
    },

    closeThread: (threadId: string) => {
      const now = new Date().toISOString();
      const wasCurrent = get().currentThreadId === threadId;
      // The run stops FIRST — on screen or in the background — settled and
      // stored while the thread is still open; then the thread closes. The
      // other order let the settle's store re-open what was just closed.
      if (wasCurrent) endConversation();
      else get().dropParkedConversation(threadId);
      return mutate(threadId, 'close', { closedAt: now }, () => apiCloseThread(threadId), () => {
        // The server still has it open: bring the conversation back too.
        if (wasCurrent && get().currentThreadId === null) get().loadThread(threadId);
      });
    },

    reopenThread: (threadId: string) => {
      return mutate(threadId, 'reopen', { closedAt: null, lastOpenedAt: new Date().toISOString() },
        () => apiReopenThread(threadId));
    },

    deleteThread: (threadId: string) => {
      const { savedThreads, currentThreadId } = get();
      const wasCurrent = currentThreadId === threadId;
      // The run stops first, settled while the thread still exists; then the
      // thread goes. The other order would store the settled turn into a
      // thread that is already gone and bring it back. Ending the on-screen
      // conversation here also means its turns cannot be uploaded again
      // later under a fresh id.
      if (wasCurrent) endConversation();
      else get().dropParkedConversation(threadId);
      // The copy a refused delete puts back is the SETTLED one — its last
      // turn cancelled — not the one from before the run was stopped.
      const removed = get().savedThreads.find((t) => t.id === threadId);
      set({ savedThreads: get().savedThreads.filter((t) => t.id !== threadId) } as Partial<VoyagerState>);
      // A delete is final: a parked snapshot must not bring it back.
      unparkLocally(threadId);
      const gen = get().threadsGeneration;
      begin(threadId);
      queued(threadId, () => apiDeleteThread(threadId)).then(() => {
        settle(threadId);
        // The settle's persist ran ahead of this delete on the same queue;
        // had it failed and parked a copy, that copy goes too. A delete is
        // final on every path.
        unparkLocally(threadId);
      }, (err) => {
        settle(threadId);
        if (err instanceof StaleWorkspaceError) return;
        warn('delete', err);
        // The server still has it, so the list must too — otherwise the
        // thread looks gone until a reload brings it back.
        if (removed && get().threadsGeneration === gen && !get().savedThreads.some((t) => t.id === threadId)) {
          replaceLocal(removed);
          if (wasCurrent && get().currentThreadId === null) get().loadThread(threadId);
        }
      });
    },

    addThreadToSpace: (threadId: string, spaceId: string | null) => {
      mutate(threadId, 'space', { spaceId }, () => patchThread(threadId, { space_id: spaceId }));
    },

    createAgentChatThread: async (thread: ConversationThread) => {
      const id = thread.id;
      replaceLocal(thread);
      const gen = get().threadsGeneration;
      begin(id);
      try {
        const row = await queued(id, () => upsertThread(id, {
          kind: thread.kind as ChatKind, title: thread.title, source_kind: thread.sourceKind ?? 'agent', source_id: thread.sourceId ?? null, snapshot: { turns: [] },
        }));
        settle(id);
        if (get().threadsGeneration !== gen) return false;
        applyServerStamp(id, row);
        return true;
      } catch (err) {
        settle(id);
        if (err instanceof StaleWorkspaceError) return false;
        warn('create agent chat', err);
        if (get().threadsGeneration !== gen) return false;
        // The server never took it: the tab goes, or every turn would 404.
        set({
          savedThreads: get().savedThreads.filter((t) => t.id !== id),
          ...(get().currentThreadId === id ? { currentThreadId: null } : {}),
        } as Partial<VoyagerState>);
        throw err;
      }
    },

    persistAgentChat: (threadId: string, turns: any[]) => {
      const existing = get().savedThreads.find((t) => t.id === threadId);
      if (!existing || !isChatKind(existing.kind)) return;
      const last = [...turns].reverse().find((t) => t?.role === 'agent');
      patchLocal(threadId, { landingAgentTurns: turns, preview: String(last?.text || '').slice(0, 240) });
      // The newest snapshot replaces whatever waited: a retry timer for an
      // older one is dropped, since this write carries everything it had.
      const prev = chatOutbox.get(threadId);
      if (prev?.timer) clearTimeout(prev.timer);
      chatOutbox.set(threadId, {
        turns, attempt: 0, timer: null, ns: getPersistenceNamespace(), title: existing.title,
        source: { kind: existing.sourceKind ?? 'agent', id: existing.sourceId ?? null },
        kind: existing.kind as ChatKind,          // a reset parks the entry as what it is: a group chat stays a group chat
      });
      flushAgentChat(threadId);
    },
  };
};
